import { useMemo } from "react";
import { usePayrollRosterVersion } from "@/payroll/roster/query";
import { buildTechnicianNameMap } from "./technician-names";

/**
 * Names for the technicians in a run's adjustment rollup.
 *
 * The adjustments API returns only `employee_id` — the external ServiceTitan id —
 * so on its own the table can only say `#430353700`, which tells nobody whose pay
 * they are about to send. The payroll roster snapshot carries both halves
 * (`service_titan_employee_id` and `display_name`), so the name comes from there.
 *
 * Deliberately the run's roster VERSION rather than the live roster: the run was
 * calculated against that snapshot, so it is the naming that matches the numbers
 * on screen — and a technician renamed since doesn't retroactively relabel a
 * period's push. The pay-breakdown page already prefetches this exact query for
 * the Roster tab, so it is normally a cache read.
 *
 * A technician with adjustments but no roster row simply keeps their id. That is a
 * real case (someone paid a spiff who was never rostered), not an error, so it
 * fails soft rather than blocking the table.
 */
export const useTechnicianNames = (
  businessId: string | null,
  rosterVersionNumber: number | null,
  enabled = true,
) => {
  const { data: roster } = usePayrollRosterVersion(
    businessId,
    rosterVersionNumber,
    enabled,
  );

  const byExternalId = useMemo(
    () => buildTechnicianNameMap(roster?.members),
    [roster?.members],
  );

  return byExternalId;
};
